import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Video, Plus, Pencil, Trash2, Play, Radio, ShieldAlert, Loader2, Users, KeyRound,
  Wifi, WifiOff, RefreshCw,
} from "lucide-react";

const DAYS = [{ n: 1, s: "Mon" }, { n: 2, s: "Tue" }, { n: 3, s: "Wed" }, { n: 4, s: "Thu" }, { n: 5, s: "Fri" }, { n: 6, s: "Sat" }, { n: 0, s: "Sun" }];
const CLASSES = ["Play Group", "LKG", "UKG", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];
const SECTIONS = ["A", "B", "C", "D"];

export default function AdminLiveClassroom() {
  const [rows, setRows] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // camera being edited/created
  const [pinDlg, setPinDlg] = useState(null);      // { student_id } for PIN dialog
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/live-cameras");
      setRows(data.cameras || []);
      setSettings(data.settings || null);
    } catch { toast.error("Could not load live-camera list."); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({
    camera_name: "", class_name: "", section: "", rtsp_url: "",
    stream_path: "", audio_enabled: false, enabled: true, location: "",
    schedule: { days: [1, 2, 3, 4, 5, 6], start: "09:00", end: "16:00" },
  });

  const openEdit = async (r) => {
    try {
      const { data } = await api.get(`/admin/live-cameras/${r.id}`);
      setEditing(data);
    } catch { toast.error("Could not load camera."); }
  };

  const save = async () => {
    if (!editing) return;
    if (!editing.class_name || !editing.section) { toast.error("Pick class and section."); return; }
    setBusy(true);
    try {
      const payload = { ...editing };
      if (editing.id) await api.put(`/admin/live-cameras/${editing.id}`, payload);
      else await api.post("/admin/live-cameras", payload);
      toast.success("Camera saved.");
      setEditing(null); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
    finally { setBusy(false); }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete camera "${r.camera_name}"? Active sessions will be ended.`)) return;
    try { await api.delete(`/admin/live-cameras/${r.id}`); toast.success("Deleted."); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed."); }
  };

  const test = async (r) => {
    try {
      const { data } = await api.post(`/admin/live-cameras/${r.id}/test`);
      if (data.reachable) toast.success(`Camera online (${data.gateway}/${data.path})`);
      else toast.error(`Offline — ${data.reason || `HTTP ${data.http_status}`}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed."); }
  };

  const emergencyDisable = async () => {
    if (!window.confirm("Disable ALL live streaming immediately? Every active parent session will be revoked.")) return;
    try { await api.post("/admin/live-streaming/emergency-disable"); toast.success("All streams stopped."); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
  };

  const toggleEmergency = async (val) => {
    try {
      await api.put("/admin/live-streaming/settings", { emergency_disabled: val });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
  };

  const savePin = async ({ student_id, pin }) => {
    try {
      await api.post("/admin/live-streaming/parent-pin", { student_id, pin });
      toast.success("Parent PIN saved.");
      setPinDlg(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "PIN save failed."); }
  };

  const totalViewers = useMemo(() => rows.reduce((s, r) => s + (r.current_viewers || 0), 0), [rows]);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto space-y-4" data-testid="admin-live-classroom">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 text-xl font-semibold"><Video className="h-6 w-6 text-rose-500" /> Live Classroom Management</div>
            <p className="text-sm text-slate-500">Add classroom cameras, control viewing schedule and privacy, and monitor live viewers.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" className="rounded-full" onClick={load} data-testid="live-reload"><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
            <Button variant="outline" className="rounded-full text-rose-600 border-rose-200 hover:bg-rose-50" onClick={emergencyDisable} data-testid="live-emergency"><ShieldAlert className="h-4 w-4 mr-1" /> Emergency Disable All</Button>
            <Button className="rounded-full bg-rose-500 hover:bg-rose-600" onClick={openNew} data-testid="live-new"><Plus className="h-4 w-4 mr-1" /> Add camera</Button>
          </div>
        </div>

        <SettingsCard settings={settings} onToggleEmergency={toggleEmergency} onSave={async (s) => { await api.put("/admin/live-streaming/settings", s); await load(); toast.success("Settings updated."); }} totalViewers={totalViewers} />

        {loading ? (
          <div className="py-16 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
        ) : rows.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 p-10 text-center text-sm text-slate-500" data-testid="live-empty">
            No cameras yet — tap <span className="font-semibold">Add camera</span> to map your first classroom.
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 divide-y divide-slate-100 dark:divide-slate-800 overflow-hidden">
            {rows.map((r) => (
              <div key={r.id} className="p-4 flex flex-wrap items-center gap-4" data-testid={`live-row-${r.id}`}>
                <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${r.enabled ? "bg-emerald-100 text-emerald-600 dark:bg-emerald-950/40" : "bg-slate-100 text-slate-400 dark:bg-slate-800"}`}>
                  {r.status === "online" ? <Wifi className="h-5 w-5" /> : r.status === "offline" ? <WifiOff className="h-5 w-5" /> : <Video className="h-5 w-5" />}
                </div>
                <div className="flex-1 min-w-[200px]">
                  <div className="font-semibold flex items-center gap-2">
                    {r.camera_name}
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${r.enabled ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" : "bg-slate-100 text-slate-500 dark:bg-slate-800"}`}>{r.enabled ? "Enabled" : "Disabled"}</span>
                    {r.status ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 uppercase">{r.status}</span> : null}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Class {r.class_name}-{r.section} · Path <code>{r.stream_path}</code>{r.audio_enabled ? " · Audio ON" : ""}
                  </div>
                  <div className="text-[11px] text-slate-400 mt-0.5">Viewing: {(r.schedule?.days || []).map((d) => DAYS.find((x) => x.n === d)?.s).filter(Boolean).join("·") || "Every day"} · {r.schedule?.start || "00:00"}–{r.schedule?.end || "23:59"}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-lg font-bold flex items-center justify-end gap-1 text-rose-500"><Users className="h-4 w-4" />{r.current_viewers || 0}</div>
                  <div className="text-[10px] text-slate-400 uppercase">viewers</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => test(r)} data-testid={`live-test-${r.id}`}><Play className="h-3.5 w-3.5 mr-1" /> Test</Button>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => openEdit(r)} data-testid={`live-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="outline" className="rounded-full text-rose-500" onClick={() => del(r)} data-testid={`live-del-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <PinCard onOpen={(sid) => setPinDlg({ student_id: sid })} />
      </div>

      {editing ? (
        <CameraDialog
          value={editing}
          onChange={setEditing}
          onClose={() => setEditing(null)}
          onSave={save}
          busy={busy}
        />
      ) : null}

      {pinDlg ? (
        <PinDialog student_id={pinDlg.student_id} onClose={() => setPinDlg(null)} onSave={savePin} />
      ) : null}
    </Layout>
  );
}

function SettingsCard({ settings, onToggleEmergency, onSave, totalViewers }) {
  const [local, setLocal] = useState(settings || {});
  useEffect(() => setLocal(settings || {}), [settings]);
  if (!settings) return null;
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 grid md:grid-cols-4 gap-4 items-center" data-testid="live-settings">
      <div>
        <div className="text-[10px] uppercase tracking-wide text-slate-500">Total live viewers</div>
        <div className="text-2xl font-bold text-rose-500 flex items-center gap-1"><Users className="h-5 w-5" />{totalViewers}</div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wide text-slate-500">Gateway</div>
        <div className="text-sm font-mono truncate" title={settings.gateway_configured ? "Configured" : "Not configured"}>
          {settings.gateway_configured ? "✓ Configured" : "⚠︎ Not set — check LIVE_STREAM_GATEWAY_URL"}
        </div>
        <div className="text-[11px] text-slate-500 uppercase">{settings.gateway_protocol}</div>
      </div>
      <div className="flex items-center gap-3">
        <Switch checked={!!settings.parent_pin_required} onCheckedChange={(v) => onSave({ parent_pin_required: v })} data-testid="live-pin-toggle" />
        <div>
          <div className="text-sm font-medium">Parent PIN required</div>
          <div className="text-[11px] text-slate-500">Extra safety for parent view</div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <Switch checked={!!settings.emergency_disabled} onCheckedChange={onToggleEmergency} data-testid="live-emergency-toggle" />
        <div>
          <div className="text-sm font-medium">Emergency disable</div>
          <div className="text-[11px] text-slate-500">Instantly stops all streams</div>
        </div>
      </div>
      <div className="md:col-span-4 flex items-center gap-3">
        <Label className="text-[11px] uppercase text-slate-500">Max devices / student</Label>
        <Input
          type="number" min={1} max={10}
          value={local.max_devices_per_student ?? 2}
          onChange={(e) => setLocal((s) => ({ ...s, max_devices_per_student: parseInt(e.target.value, 10) || 1 }))}
          onBlur={() => onSave({ max_devices_per_student: local.max_devices_per_student })}
          className="w-20 rounded-lg" data-testid="live-max-devices"
        />
      </div>
    </div>
  );
}

function CameraDialog({ value, onChange, onClose, onSave, busy }) {
  const v = value || {};
  const set = (k, x) => onChange({ ...v, [k]: x });
  const setSched = (k, x) => onChange({ ...v, schedule: { ...(v.schedule || {}), [k]: x } });
  const days = new Set((v.schedule?.days || []));
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="live-camera-dialog">
        <DialogHeader><DialogTitle>{v.id ? "Edit camera" : "Add classroom camera"}</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Camera name</Label>
            <Input value={v.camera_name || ""} onChange={(e) => set("camera_name", e.target.value)} placeholder="Class 6-A Front" className="rounded-xl mt-1" data-testid="lcd-name" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Class</Label>
              <Select value={v.class_name || ""} onValueChange={(x) => set("class_name", x)}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="lcd-class"><SelectValue placeholder="Class" /></SelectTrigger>
                <SelectContent>{CLASSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Section</Label>
              <Select value={v.section || ""} onValueChange={(x) => set("section", x)}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="lcd-section"><SelectValue placeholder="Section" /></SelectTrigger>
                <SelectContent>{SECTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">RTSP URL (never sent to browser)</Label>
            <Input value={v.rtsp_url || ""} onChange={(e) => set("rtsp_url", e.target.value)} placeholder="rtsp://user:pass@192.168.0.10:554/…" className="rounded-xl mt-1 font-mono text-xs" data-testid="lcd-rtsp" />
            <p className="text-[11px] text-slate-500 mt-1">Only the media gateway uses this. Stored encrypted on the server.</p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Gateway path</Label>
            <Input value={v.stream_path || ""} onChange={(e) => set("stream_path", e.target.value)} placeholder="class-6-a" className="rounded-xl mt-1 font-mono text-xs" data-testid="lcd-path" />
            <p className="text-[11px] text-slate-500 mt-1">Must match the <code>path</code> configured in MediaMTX (or your gateway).</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center gap-2 text-sm"><Switch checked={!!v.enabled} onCheckedChange={(x) => set("enabled", x)} data-testid="lcd-enabled" /> Enabled</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={!!v.audio_enabled} onCheckedChange={(x) => set("audio_enabled", x)} data-testid="lcd-audio" /> Audio</label>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Viewing schedule</Label>
            <div className="flex flex-wrap gap-1 mt-1">
              {DAYS.map(({ n, s }) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => {
                    const next = new Set(days);
                    if (next.has(n)) next.delete(n); else next.add(n);
                    setSched("days", Array.from(next));
                  }}
                  className={`text-[11px] px-2.5 py-1 rounded-full border ${days.has(n) ? "bg-rose-500 text-white border-rose-500" : "border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"}`}
                  data-testid={`lcd-day-${n}`}
                >{s}</button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <Input type="time" value={v.schedule?.start || "09:00"} onChange={(e) => setSched("start", e.target.value)} className="rounded-lg" data-testid="lcd-start" />
              <Input type="time" value={v.schedule?.end || "16:00"} onChange={(e) => setSched("end", e.target.value)} className="rounded-lg" data-testid="lcd-end" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="lcd-cancel">Cancel</Button>
          <Button onClick={onSave} disabled={busy} className="bg-rose-500 hover:bg-rose-600" data-testid="lcd-save">
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Save camera
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PinCard({ onOpen }) {
  const [sid, setSid] = useState("");
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 flex flex-wrap items-center gap-3" data-testid="live-pin-card">
      <div className="flex items-center gap-2 flex-1 min-w-[240px]">
        <KeyRound className="h-5 w-5 text-amber-500" />
        <div>
          <div className="font-medium text-sm">Set / reset Parent PIN</div>
          <div className="text-[11px] text-slate-500">Parents must enter this PIN before watching the Live Classroom.</div>
        </div>
      </div>
      <Input value={sid} onChange={(e) => setSid(e.target.value.trim())} placeholder="Student ID (e.g. TRS-6A-05)" className="rounded-xl flex-1 min-w-[220px]" data-testid="live-pin-sid" />
      <Button variant="outline" className="rounded-full" disabled={!sid} onClick={() => onOpen(sid)} data-testid="live-pin-open">Set PIN…</Button>
    </div>
  );
}

function PinDialog({ student_id, onSave, onClose }) {
  const [pin, setPin] = useState("");
  const [pin2, setPin2] = useState("");
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="pin-dialog">
        <DialogHeader><DialogTitle>Parent PIN — {student_id}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase text-slate-500">4–8 digit PIN</Label>
            <Input type="password" inputMode="numeric" pattern="[0-9]*" value={pin} onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, ""))} className="rounded-xl mt-1" maxLength={8} data-testid="pin-a" />
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">Confirm PIN</Label>
            <Input type="password" inputMode="numeric" pattern="[0-9]*" value={pin2} onChange={(e) => setPin2(e.target.value.replace(/[^0-9]/g, ""))} className="rounded-xl mt-1" maxLength={8} data-testid="pin-b" />
          </div>
          <p className="text-[11px] text-slate-500">Share this PIN privately with the parent. It is stored hashed on the server.</p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            className="bg-rose-500 hover:bg-rose-600"
            disabled={pin.length < 4 || pin !== pin2}
            onClick={() => onSave({ student_id, pin })}
            data-testid="pin-save"
          >Save PIN</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
